import { useState, useRef, useEffect } from "react";

type FAQ = { q: string; a: string };
type Category = { label: string; icon: string; faqs: FAQ[] };

const categories: Category[] = [
  {
    label: "Getting Started",
    icon: "ri-rocket-line",
    faqs: [
      { q: "What is Aurays?", a: "Aurays is an online-to-offline (O2O) dating and community app based in Hong Kong. We help people meet through shared interests, build natural connections, and transition safely into real-life meetings." },
      { q: "Is it free to use?", a: "Yes! Aurays is free to join. We also offer a premium tier with additional features. Early access members get 2 months of premium absolutely free." },
      { q: "How do I sign up?", a: "Fill in our early access registration form on this website. Once we launch, you'll receive an invite link via email and be first in line." },
    ],
  },
  {
    label: "Matching",
    icon: "ri-heart-line",
    faqs: [
      { q: "How does swiping work?", a: "Our AI algorithm matches you based on interests, personality, and behavioural signals — not just photos. The more you use Aurays, the smarter your matches become." },
      { q: "Do matches expire?", a: "Matches stay active as long as both users are engaged. Inactive matches may expire after 30 days to keep the experience fresh and meaningful." },
    ],
  },
  {
    label: "Communities",
    icon: "ri-group-line",
    faqs: [
      { q: "What are communities?", a: "Communities are interest-based groups — think hiking, photography, food, music, and more. They're a natural way to discover people before dating intent comes into play." },
      { q: "Can I DM someone in a community?", a: "You can like profiles and start conversations once there's a mutual interest. This keeps interactions respectful and pressure-free." },
    ],
  },
  {
    label: "Events",
    icon: "ri-calendar-event-line",
    faqs: [
      { q: "How do I join events?", a: "Browse curated events in your area, RSVP, and show up. Your reputation score may affect which events you can access — higher scores unlock more intimate gatherings." },
      { q: "What happens after an event?", a: "After an event, you can connect with attendees you met in person. You'll also be prompted to rate your experience to help maintain community standards." },
    ],
  },
  {
    label: "Safety",
    icon: "ri-shield-check-line",
    faqs: [
      { q: "How does the reputation system work?", a: "Users earn a 1–5 star reputation based on their behaviour, ratings received from others, and engagement quality. Higher ratings unlock access to exclusive features and events." },
      { q: "How do I report someone?", a: "Every profile has a report button. Our moderation team reviews all reports within 24 hours. Serious violations result in immediate suspension." },
    ],
  },
  {
    label: "Premium",
    icon: "ri-vip-crown-line",
    faqs: [
      { q: "What does Premium include?", a: "Premium includes unlimited likes, advanced filters, priority event access, read receipts, and a profile boost. Early access members get 2 months free." },
      { q: "What are the pricing plans?", a: "We'll announce detailed pricing at launch. Early access members are guaranteed the best possible rate as a thank-you for being first." },
    ],
  },
  {
    label: "Privacy",
    icon: "ri-lock-line",
    faqs: [
      { q: "How is my data protected?", a: "Your data is encrypted in transit and at rest. We follow strict data minimisation principles and never sell your personal information to third parties." },
      { q: "Do you sell my data?", a: "Never. Your data belongs to you. We use it only to improve your experience within Aurays. You can request deletion of your data at any time." },
    ],
  },
];

function AccordionItem({ faq }: { faq: FAQ }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-semibold text-gray-900 pr-4">{faq.q}</span>
        <i className={`ri-arrow-down-s-line text-gray-400 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}></i>
      </button>
      <div
        className={`transition-all duration-300 overflow-hidden ${open ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <p className="px-6 pb-5 text-sm text-gray-500 leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" ref={ref} className="bg-[#F5F0FF] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-xs font-semibold text-[#7C55CC] uppercase tracking-widest mb-3">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Common questions</h2>
        </div>

        <div className={`flex flex-col lg:flex-row gap-8 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Category nav */}
          <div className="lg:w-56 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {categories.map((cat, i) => (
              <button
                key={i}
                onClick={() => setActiveCategory(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === i
                    ? "bg-[#7C55CC] text-white"
                    : "bg-white text-gray-500 hover:text-gray-900 border border-gray-200"
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.label}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="flex-1 flex flex-col gap-3">
            {categories[activeCategory].faqs.map((faq, i) => (
              <AccordionItem key={i} faq={faq} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
